import { Box, Typography, IconButton, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useId, useState, useRef } from "react";
import { getPreviewUrl } from "~/shared/utils/uploadImage";
import { useSnackbar } from "notistack";
import { validateImageFile } from "~/shared/utils/validateImage";

type Props = {
  value: (File | string)[];
  onFiles: (files: File[]) => Promise<void> | void;
  onRemove: (index: number) => void;
  uploading?: { done: number; total: number } | null;
  maxFilesPerBatch?: number;
};

export const LogoDropzone: React.FC<Props> = ({ value, onFiles, onRemove, uploading, maxFilesPerBatch = 30 }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputId = useId();
  const [isDragActive, setIsDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleFiles = async (filesList: FileList | null) => {
    if (!filesList) return;

    const filesArray = Array.from(filesList);
    if (!filesArray.length) return;

    const normalizedFiles = filesArray.slice(0, maxFilesPerBatch);
    if (filesArray.length > maxFilesPerBatch) {
      enqueueSnackbar(`Можно загрузить до ${maxFilesPerBatch} файлов за раз`, {
        variant: "warning",
        autoHideDuration: 3000,
      });
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of normalizedFiles) {
      const error = await validateImageFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      enqueueSnackbar(`Некоторые файлы пропущены:\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? "\n..." : ""}`, {
        variant: "warning",
        autoHideDuration: 5000,
        style: { maxWidth: "800px" },
      });
    }

    if (validFiles.length) {
      await onFiles(validFiles);
    }
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    setIsDragActive(false);
    void handleFiles(e.dataTransfer.files);
  };

  const handleSelect: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.files && e.target.files.length) {
      void handleFiles(e.target.files);
    }
    e.target.value = "";
  };

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Box
        component="label"
        htmlFor={inputId}
        onDragOver={e => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "divider",
          borderRadius: 1,
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          position: "relative",
          bgcolor: isDragActive ? "action.hover" : "transparent",
          transition: "all 0.2s ease",
        }}
      >
        <Typography>Перетащите файлы сюда или нажмите для выбора</Typography>
        <Typography variant="caption" color="text.secondary">
            Поддерживаемые форматы: JPG, PNG
        </Typography>
        <input
          id={inputId}
          type="file"
          accept="image/png, image/jpeg"
          multiple
          onChange={handleSelect}
          ref={inputRef}
          style={{ display: "none" }}
        />
      </Box>

      {uploading && uploading.total > 0 && (
        <Box mt={1}>
          <Typography variant="caption">Загрузка: {uploading.done}/{uploading.total}</Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.round((uploading.done / uploading.total) * 100))}
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {value?.length ? (
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {value.map((item, idx) => {
            const src = getPreviewUrl(item);
            const backendUrl = import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_URL || '';
            return (
              <Box key={idx} sx={{ position: "relative", width: 60, height: 60 }}>
                <Box
                  component="img"
                  src={src.includes("http") ? src : `${backendUrl}${src}`}
                  sx={{ width: 1, height: 1, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                />
                <IconButton
                  size="small"
                  onClick={() => onRemove(idx)}
                  sx={{ position: "absolute", top: -10, right: -10, bgcolor: "background.paper" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};
